// MFA Challenge
// --------------
// Second-factor step shown after a valid password, while the pool runs
// MfaConfiguration=REQUIRED with TOTP (SOFTWARE_TOKEN_MFA) as the only factor.
//
// Two modes, driven by the challenge Cognito returned (passed in nav state):
//   - "verify" (SOFTWARE_TOKEN_MFA): a returning, already-enrolled user enters
//     the current 6-digit code from their authenticator.
//   - "setup"  (MFA_SETUP): a first-time user (existing users on first login
//     under REQUIRED, or a freshly-activated invitee) scans a QR code, adds it
//     to their authenticator, then enters the first code to finish enrolment.
//
// The pending challenge user lives in the CognitoLoginClass singleton (a ref in
// App that survives this navigation). A hard refresh drops it, so we gate on
// cognitologin.isMfaPending() and bounce back to login if the challenge is gone.

import React, { useState, useCallback, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, KeyRound, Loader2, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import {
  EMPTY_STRING,
  LOGIN_PAGE,
  MFA_SETUP,
  NIMIS_WEBSITE_URL,
  SOFTWARE_TOKEN_MFA,
} from "@/constants/definitions";
import CognitoLoginClass from "@/models/cognitoLogin";
import { loadDashboard } from "@/utils/navigation";
import { persistPreferredUsername } from "@/services/dataListOperations.service";

// Cognito TOTP codes are 6 digits. Constrain input so an obviously-wrong length
// can't be submitted, and so the field reads as a one-time code to password
// managers (autoComplete="one-time-code", inputMode numeric).
const TOTP_CODE_LENGTH = 6;
const TOTP_CODE_PATTERN = /^\d{6}$/;

type MfaMode = "verify" | "setup";

interface MfaNavState {
  mode?: MfaMode;
  // Label shown in the authenticator app entry (the user's email/username).
  accountLabel?: string;
  // Preferred username chosen during activation, to persist once MFA setup
  // establishes a session. Only present when arriving from the activate flow.
  pendingDisplayName?: string;
}

interface MfaChallengeProps {
  cognitologin: CognitoLoginClass;
  getUserDetails: () => Promise<void>;
  navigate: (path: string) => void;
  location: { state?: MfaNavState };
  /** Same bloom hooks Login uses, so entering the app after MFA matches the
   *  normal sign-in transition. Optional — falls back to a direct navigation. */
  onRevealStart?: () => void;
  onTransitionStart?: () => Promise<void>;
}

export default function MfaChallenge({
  cognitologin,
  getUserDetails,
  navigate,
  location,
  onRevealStart,
  onTransitionStart,
}: MfaChallengeProps) {
  const navState = (location?.state ?? {}) as MfaNavState;
  const mode: MfaMode = navState.mode === "setup" ? "setup" : "verify";
  const accountLabel = navState.accountLabel || "account";

  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Setup-mode enrolment material (QR + manual-entry secret).
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [manualSecret, setManualSecret] = useState<string | null>(null);
  const [setupLoading, setSetupLoading] = useState(mode === "setup");
  const [setupFailed, setSetupFailed] = useState(false);

  // If there's no challenge in progress (e.g. the page was refreshed and the
  // in-memory challenge user was lost), there's nothing to complete — send the
  // user back to sign in. Rendered below via <Navigate/>.
  const challengeMissing = !cognitologin.isMfaPending();

  // Kick off TOTP enrolment once, in setup mode, to fetch the secret + QR.
  useEffect(() => {
    if (mode !== "setup" || challengeMissing) return;
    let cancelled = false;
    (async () => {
      const enrolment = await cognitologin.beginTotpSetup(accountLabel);
      if (cancelled) return;
      if (!enrolment) {
        setSetupFailed(true);
      } else {
        setOtpauthUri(enrolment.otpauthUri);
        setManualSecret(enrolment.secret);
      }
      setSetupLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, challengeMissing, cognitologin, accountLabel]);

  // Shared success path — mirrors Login's EMPTY_STRING branch so the entry
  // transition is identical whether the user hit MFA or signed in directly.
  const enterDashboard = useCallback(async () => {
    setIsTransitioning(true);
    onRevealStart?.();
    await getUserDetails();
    await (onTransitionStart?.() ?? Promise.resolve());
    loadDashboard(navigate);
  }, [getUserDetails, navigate, onRevealStart, onTransitionStart]);

  const handleSubmit = useCallback(async () => {
    if (!TOTP_CODE_PATTERN.test(code)) {
      setErrorMessage(`Enter the ${TOTP_CODE_LENGTH}-digit code from your authenticator app`);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const result =
        mode === "setup"
          ? await cognitologin.confirmTotpSetup(code)
          : await cognitologin.confirmMfaCode(code);

      if (result === EMPTY_STRING) {
        setIsSubmitting(false);
        // Fresh activation via the MFA path: persist the preferred username now
        // that setup has established a session. Non-fatal — don't block entry.
        if (mode === "setup" && navState.pendingDisplayName) {
          await persistPreferredUsername(navState.pendingDisplayName).catch(() => {});
        }
        await enterDashboard();
      } else {
        setErrorMessage(result);
        setCode("");
      }
    } catch (submitError) {
      setErrorMessage("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [code, mode, cognitologin, enterDashboard]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") handleSubmit();
    },
    [handleSubmit]
  );

  if (challengeMissing) {
    return <Navigate to={LOGIN_PAGE} replace />;
  }

  const heading = mode === "setup" ? "Set up two-factor authentication" : "Two-factor authentication";
  const subtext =
    mode === "setup"
      ? "Scan the QR code with an authenticator app (Google Authenticator, Authy, 1Password…), then enter the 6-digit code to finish."
      : "Enter the 6-digit code from your authenticator app.";

  return (
    <div
      data-theme="dark"
      className="relative"
      style={{
        background: isTransitioning ? "hsla(222, 70%, 8%, 0)" : "hsla(222, 70%, 8%, 0.85)",
        transition: "background 450ms ease-out",
        minHeight: "calc(100vh + 1px)",
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 20% 30%, rgba(59,130,246,0.08) 0%, transparent 70%), radial-gradient(ellipse 50% 40% at 80% 70%, rgba(14,165,233,0.05) 0%, transparent 70%)",
          opacity: isTransitioning ? 0 : 1,
          transition: "opacity 350ms ease-out",
        }}
      />

      <div
        className="relative z-10 mx-auto flex w-full max-w-6xl items-center px-4 sm:px-6 pt-6 pb-6"
        style={{ opacity: isTransitioning ? 0 : 1, transition: "opacity 350ms ease-out" }}
      >
        <a href={NIMIS_WEBSITE_URL}>
          <img src="/nimis-intelligence-white-logo.png" alt="NIMIS Intelligence" className="h-12 w-auto" />
        </a>
      </div>

      <div
        className="relative z-10 flex justify-center pt-[10vh]"
        style={{
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? "translateY(-16px) scale(0.96)" : "translateY(0) scale(1)",
          transition: "opacity 400ms ease-out, transform 400ms ease-out",
        }}
      >
        <div className="w-full max-w-sm mx-4">
          <div className="rounded-xl bg-white/[0.03] backdrop-blur-sm border border-white/[0.08] p-8">
            <div className="text-center mb-6">
              <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-blue-500/10 border border-blue-500/20">
                <ShieldCheck className="h-5 w-5 text-blue-400" />
              </div>
              <h1 className="text-xl font-semibold text-white">{heading}</h1>
              <p className="mt-1.5 text-sm text-white/50">{subtext}</p>
            </div>

            {errorMessage && (
              <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
                {errorMessage}
              </div>
            )}

            {/* Setup mode: QR + manual secret */}
            {mode === "setup" && (
              <div className="mb-5">
                {setupLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-white/40" />
                  </div>
                ) : setupFailed ? (
                  <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
                    Couldn't start MFA setup. Please return to sign in and try again.
                  </div>
                ) : (
                  <>
                    <div className="flex justify-center">
                      <div className="rounded-lg bg-white p-3">
                        {otpauthUri && <QRCodeSVG value={otpauthUri} size={160} />}
                      </div>
                    </div>
                    {manualSecret && (
                      <div className="mt-3 text-center">
                        <p className="text-xs text-white/40">Can't scan? Enter this key manually:</p>
                        <p className="mt-1 font-mono text-xs text-white/70 break-all select-all">
                          {manualSecret}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="space-y-4">
              <FormField label="Verification code" htmlFor="totpCode" variant="dark">
                <Input
                  id="totpCode"
                  type="text"
                  inputMode="numeric"
                  variant="dark"
                  placeholder="123456"
                  value={code}
                  onChange={(changeEvent) => {
                    // Digits only, capped at 6 — keeps the field aligned with the
                    // TOTP format and strips accidental spaces from copy/paste.
                    const digits = changeEvent.target.value.replace(/\D/g, "").slice(0, TOTP_CODE_LENGTH);
                    setCode(digits);
                    setErrorMessage("");
                  }}
                  onKeyDown={handleKeyDown}
                  autoComplete="one-time-code"
                  maxLength={TOTP_CODE_LENGTH}
                  disabled={isSubmitting || isTransitioning || (mode === "setup" && (setupLoading || setupFailed))}
                  leftIcon={<KeyRound className="h-4 w-4" />}
                />
              </FormField>

              <Button
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  isTransitioning ||
                  code.length !== TOTP_CODE_LENGTH ||
                  (mode === "setup" && (setupLoading || setupFailed))
                }
                className="w-full h-10 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-colors disabled:bg-blue-600/50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : mode === "setup" ? (
                  "Verify & continue"
                ) : (
                  "Verify"
                )}
              </Button>
            </div>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={async () => {
                  // Abandon the challenge cleanly: clear the half-open Cognito
                  // sign-in so a fresh login starts from scratch.
                  await cognitologin.logout();
                  navigate(LOGIN_PAGE);
                }}
                className="inline-flex items-center gap-1.5 text-sm text-white/40 hover:text-white/70 transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
